/**
 * ML Shipping Rules (centralized)
 * Single source of truth for SelectUrns shipping-window calculations.
 *
 * VERSION: 2025-12-15 (holiday + TB custom + TB shipping update)
 *
 * IMPORTANT IMPLEMENTATION NOTES
 * - All date math is performed using UTC "date-only" objects that represent
 *   the America/Chicago calendar day (avoids DST edge-cases).
 *
 * HOLIDAYS / NON-BUSINESS DAYS (TB & CW)
 * These days do NOT count as business days (Mon–Fri). If encountered while
 * adding business days, we skip them (effectively adding +1 business day).
 *   • New Year’s Day (observed)
 *   • MLK Day (3rd Monday in January)
 *   • Memorial Day (last Monday in May)
 *   • Independence Day (observed)
 *   • Labor Day (1st Monday in September)
 *   • Thanksgiving (4th Thursday in November)
 *   • Day after Thanksgiving (Friday after Thanksgiving)
 *   • Christmas Eve (Dec 24)
 *   • Christmas Day (observed)
 *   • New Year’s Eve (Dec 31)
 *
 * BUSINESS RULES
 * - TB urns:
 *   • Cutoff (business days only): 2:00 PM America/Chicago
 *   • Processing time (business days):
 *       - TB + CUSTOM     -> 2 business days
 *       - TB (not CUSTOM) -> 1 business day
 *     If ordered at/after cutoff on a BUSINESS DAY, add +1 business day.
 *   • Shipping: 2–4 BUSINESS DAYS from the LATEST processing date (ready/ship date).
 *
 * - CW urns (unchanged timing, new holiday list applies):
 *   • Processing: 3–5 business days
 *   • Shipping: 3–5 business days
 *   • "Ready" date is latest processing completion date (today + 5 business days).
 *   • Delivery window:
 *       earliest = today + 3 business days (processing) + 3 business days (shipping)
 *       latest   = today + 5 business days (processing) + 5 business days (shipping)
 *
 * HOW TO INDICATE CUSTOM (TB only)
 * - Preferred: pass { isCustom: true } or { tags: ['TB','CUSTOM', ...] } to calculateWindow().
 * - Backward-compatible fallback: if not provided, this file will TRY to infer tags by
 *   scanning for Shopify product JSON in <script type="application/json" id="ProductData-*">.
 */

(function (root) {
  'use strict';

  // Robust globals (prevents ReferenceError in SSR/tests)
  var win = (root && root.window) ? root.window : root;
  var doc = (root && root.document) ? root.document : (win && win.document ? win.document : null);

  if (!win) return;

  // Guard against double-definition if multiple sections include this file.
  if (win.MLShippingRules && win.MLShippingRules.__initialized) {
    return;
  }

  var MLShippingRules = win.MLShippingRules || {};
  MLShippingRules.__initialized = true;
  MLShippingRules.version = '2025-12-15';

  var hasObjectCreate = (typeof Object === 'function' || typeof Object === 'object') && Object && typeof Object.create === 'function';

  // Cache holiday maps per year.
  var holidayCache = hasObjectCreate ? Object.create(null) : {};

  // Cache inferred product tags (DOM scan) so we only do it once per page.
  var inferredTagsCache = null;

  /**
   * Returns the current date/time components in America/Chicago.
   * Uses Intl.DateTimeFormat.formatToParts to avoid parsing locale strings.
   */
  function getChicagoParts(now) {
    now = now || new Date();

    // Preferred: spec-stable, avoids parsing.
    try {
      if (typeof Intl === 'object' && Intl && typeof Intl.DateTimeFormat === 'function') {
        var dtf = new Intl.DateTimeFormat('en-US', {
          timeZone: 'America/Chicago',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        });

        if (typeof dtf.formatToParts === 'function') {
          var parts = dtf.formatToParts(now);
          var map = {};
          for (var i = 0; i < parts.length; i++) {
            map[parts[i].type] = parts[i].value;
          }

          return {
            year: parseInt(map.year, 10),
            month: parseInt(map.month, 10), // 1–12
            day: parseInt(map.day, 10),
            hour: parseInt(map.hour, 10),
            minute: parseInt(map.minute, 10),
            second: parseInt(map.second, 10)
          };
        }
      }
    } catch (e) {
      // Fall through to legacy fallback
    }

    // Fallback: legacy approach (string re-parse). Kept for older browsers.
    try {
      var chicagoString = now.toLocaleString('en-US', { timeZone: 'America/Chicago' });
      var chicagoDate = new Date(chicagoString);
      if (!isNaN(chicagoDate.getTime())) {
        return {
          year: chicagoDate.getFullYear(),
          month: chicagoDate.getMonth() + 1,
          day: chicagoDate.getDate(),
          hour: chicagoDate.getHours(),
          minute: chicagoDate.getMinutes(),
          second: chicagoDate.getSeconds()
        };
      }
    } catch (e2) {
      // Fall through
    }

    // Last resort: local device time.
    return {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
      hour: now.getHours(),
      minute: now.getMinutes(),
      second: now.getSeconds()
    };
  }

  function createUTCDate(y, m, d) {
    return new Date(Date.UTC(y, m, d));
  }

  function cloneUTCDate(date) {
    return createUTCDate(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  }

  function addUTCDays(date, days) {
    var result = cloneUTCDate(date);
    result.setUTCDate(result.getUTCDate() + days);
    return result;
  }

  function isWeekend(date) {
    var day = date.getUTCDay();
    return day === 0 || day === 6;
  }

  function dateKey(date) {
    return date.getUTCFullYear() + '-' + (date.getUTCMonth() + 1) + '-' + date.getUTCDate();
  }

  function nthWeekdayOfMonth(year, monthIndex, weekday, n) {
    // weekday: 0=Sun..6=Sat, n: 1=first, 2=second...
    var date = createUTCDate(year, monthIndex, 1);
    var count = 0;
    while (date.getUTCMonth() === monthIndex) {
      if (date.getUTCDay() === weekday) {
        count++;
        if (count === n) return date;
      }
      date.setUTCDate(date.getUTCDate() + 1);
    }
    return null;
  }

  function lastWeekdayOfMonth(year, monthIndex, weekday) {
    var date = createUTCDate(year, monthIndex + 1, 0); // last day of month
    while (date.getUTCMonth() === monthIndex) {
      if (date.getUTCDay() === weekday) return date;
      date.setUTCDate(date.getUTCDate() - 1);
    }
    return null;
  }

  /**
   * Observed holiday rule for fixed-date federal holidays:
   * - If on Saturday -> observed Friday
   * - If on Sunday   -> observed Monday
   * Otherwise -> same date
   */
  function observedHoliday(baseDate) {
    var day = baseDate.getUTCDay();
    if (day === 0) return addUTCDays(baseDate, 1);  // Sunday → Monday
    if (day === 6) return addUTCDays(baseDate, -1); // Saturday → Friday
    return baseDate;
  }

  function buildUSHolidayMap(year) {
    var holidays = hasObjectCreate ? Object.create(null) : {};

    function addHoliday(date) {
      if (!date) return;
      holidays[dateKey(date)] = true;
    }

    // New Year’s Day (Jan 1, observed)
    addHoliday(observedHoliday(createUTCDate(year, 0, 1)));

    // MLK Day (third Monday in January)
    addHoliday(nthWeekdayOfMonth(year, 0, 1, 3));

    // Memorial Day (last Monday in May)
    addHoliday(lastWeekdayOfMonth(year, 4, 1));

    // Independence Day (Jul 4, observed)
    addHoliday(observedHoliday(createUTCDate(year, 6, 4)));

    // Labor Day (first Monday in September)
    addHoliday(nthWeekdayOfMonth(year, 8, 1, 1));

    // Thanksgiving (fourth Thursday in November)
    var thanksgiving = nthWeekdayOfMonth(year, 10, 4, 4);
    addHoliday(thanksgiving);

    // Day after Thanksgiving (Friday after Thanksgiving)
    if (thanksgiving) addHoliday(addUTCDays(thanksgiving, 1));

    // Christmas Eve (Dec 24)
    addHoliday(createUTCDate(year, 11, 24));

    // Christmas Day (Dec 25, observed)
    addHoliday(observedHoliday(createUTCDate(year, 11, 25)));

    // New Year’s Eve (Dec 31)
    addHoliday(createUTCDate(year, 11, 31));

    return holidays;
  }

  function isMajorHoliday(date) {
    var year = date.getUTCFullYear();

    if (!holidayCache[year]) holidayCache[year] = buildUSHolidayMap(year);

    // Needed to catch observed New Year's Day that may fall on Dec 31 of the previous year.
    if (!holidayCache[year + 1]) holidayCache[year + 1] = buildUSHolidayMap(year + 1);

    var key = dateKey(date);
    return !!(holidayCache[year][key] || holidayCache[year + 1][key]);
  }

  function isBusinessDay(date) {
    return !isWeekend(date) && !isMajorHoliday(date);
  }

  // Business days: Mon–Fri, skipping major holidays.
  function addBusinessDaysSkippingHolidays(startDate, businessDays) {
    var current = cloneUTCDate(startDate);
    var added = 0;

    while (added < businessDays) {
      current.setUTCDate(current.getUTCDate() + 1);
      if (isWeekend(current) || isMajorHoliday(current)) continue;
      added++;
    }

    return current;
  }

  // Calendar days (weekends count) but skip major holidays (kept for compatibility/debugging).
  function addDaysSkippingHolidays(startDate, days) {
    var current = cloneUTCDate(startDate);
    var added = 0;

    while (added < days) {
      current.setUTCDate(current.getUTCDate() + 1);
      if (isMajorHoliday(current)) continue;
      added++;
    }

    return current;
  }

  // ---------- Tag helpers (for TB CUSTOM detection) ----------

  function normalizeTags(tags) {
    if (!tags) return [];

    // Real arrays
    if (Object.prototype.toString.call(tags) === '[object Array]') return tags;

    // Comma-separated string
    if (typeof tags === 'string') {
      var parts = tags.split(',');
      var out = [];
      for (var i = 0; i < parts.length; i++) {
        var t = (parts[i] || '').trim();
        if (t) out.push(t);
      }
      return out;
    }

    // Array-like fallback
    if (typeof tags.length === 'number') {
      var arr = [];
      for (var j = 0; j < tags.length; j++) arr.push(tags[j]);
      return arr;
    }

    return [];
  }

  function tagsContain(tags, needle) {
    var list = normalizeTags(tags);
    var target = (needle || '').toString().trim().toUpperCase();

    for (var i = 0; i < list.length; i++) {
      var tag = list[i];
      if (tag == null) continue;
      if (tag.toString().trim().toUpperCase() === target) return true;
    }
    return false;
  }

  function inferProductTagsFromDOM() {
    if (!doc || !doc.getElementsByTagName) return null;

    try {
      var scripts = doc.getElementsByTagName('script');
      for (var i = 0; i < scripts.length; i++) {
        var s = scripts[i];
        if (!s) continue;

        var type = s.type || '';
        if (type !== 'application/json') continue;

        var id = s.id || '';
        var looksLikeProductJson =
          id.indexOf('ProductData-') === 0 ||
          id.indexOf('ProductJson') === 0 ||
          id.indexOf('ProductJSON') === 0;

        if (!looksLikeProductJson) continue;

        var raw = s.textContent || s.innerHTML || '';
        if (!raw) continue;

        try {
          var data = JSON.parse(raw);

          var tags = null;
          if (data && data.tags) tags = data.tags;
          else if (data && data.product && data.product.tags) tags = data.product.tags;

          if (tags) return tags;
        } catch (e1) {
          // Keep scanning
        }
      }
    } catch (e2) {
      // Ignore
    }

    return null;
  }

  function getInferredTagsOnce() {
    if (inferredTagsCache !== null) return inferredTagsCache;

    var tags = inferProductTagsFromDOM();
    inferredTagsCache = normalizeTags(tags);
    return inferredTagsCache;
  }

  function resolveIsCustom(input) {
    input = input || {};

    // Explicit booleans win
    if (typeof input.isCustom === 'boolean') return input.isCustom;
    if (typeof input.custom === 'boolean') return input.custom;
    if (typeof input.is_custom === 'boolean') return input.is_custom;

    // Tags provided in the call
    var providedTags = input.tags || input.productTags || input.product_tags;
    if (providedTags) return tagsContain(providedTags, 'CUSTOM');

    // Best-effort fallback: infer from product JSON on the page
    var inferred = getInferredTagsOnce();
    if (inferred && inferred.length) return tagsContain(inferred, 'CUSTOM');

    return false;
  }

  /**
   * Calculates an expected delivery window.
   */
  MLShippingRules.calculateWindow = function (input) {
    input = input || {};

    var rawTagType = (input.tagType || 'TB').toString().toUpperCase();
    var tagType = rawTagType === 'CW' ? 'CW' : 'TB';

    var cutoffHour = typeof input.cutoffHour === 'number' ? input.cutoffHour : 14;
    var cutoffMinute = typeof input.cutoffMinute === 'number' ? input.cutoffMinute : 0;

    var nowParts = getChicagoParts(input.now || new Date());
    var todayUTC = createUTCDate(nowParts.year, nowParts.month - 1, nowParts.day);

    // CW logic (unchanged timings; new holiday list applies)
    if (tagType === 'CW') {
      var earliestProcessing = addBusinessDaysSkippingHolidays(todayUTC, 3);
      var latestProcessing = addBusinessDaysSkippingHolidays(todayUTC, 5);

      var earliestArrival = addBusinessDaysSkippingHolidays(earliestProcessing, 3);
      var latestArrival = addBusinessDaysSkippingHolidays(latestProcessing, 5);

      return {
        tagType: 'CW',
        nowParts: nowParts,
        todayUTC: todayUTC,
        ready: latestProcessing,
        earliest: earliestArrival,
        latest: latestArrival
      };
    }

    // TB logic (UPDATED)
    var isCustom = resolveIsCustom(input);
    var baseProcessingDays = isCustom ? 2 : 1;

    // Cutoff applies on BUSINESS DAYS only (Mon–Fri, excluding holidays).
    var todayIsBusiness = isBusinessDay(todayUTC);
    var afterCutoff =
      todayIsBusiness &&
      (nowParts.hour > cutoffHour || (nowParts.hour === cutoffHour && nowParts.minute >= cutoffMinute));

    // After cutoff adds +1 business day to processing time.
    var processingBusinessDays = baseProcessingDays + (afterCutoff ? 1 : 0);

    // Latest processing date (ready/ship date)
    var readyDate = addBusinessDaysSkippingHolidays(todayUTC, processingBusinessDays);

    // Shipping: 2–4 BUSINESS DAYS from latest processing date
    var earliestDate = addBusinessDaysSkippingHolidays(readyDate, 2);
    var latestDate = addBusinessDaysSkippingHolidays(readyDate, 4);

    return {
      tagType: 'TB',
      isCustom: isCustom,
      nowParts: nowParts,
      todayUTC: todayUTC,
      ready: readyDate,
      earliest: earliestDate,
      latest: latestDate
    };
  };

  // Expose some internals for debugging/tests (non-breaking).
  MLShippingRules._internals = {
    getChicagoParts: getChicagoParts,
    isMajorHoliday: isMajorHoliday,
    isBusinessDay: isBusinessDay,
    addBusinessDaysSkippingHolidays: addBusinessDaysSkippingHolidays,
    addDaysSkippingHolidays: addDaysSkippingHolidays,
    resolveIsCustom: resolveIsCustom
  };

  win.MLShippingRules = MLShippingRules;

  // Signal readiness so inline section scripts can wait without polling.
  try {
    if (typeof win.dispatchEvent === 'function') {
      if (typeof win.Event === 'function') {
        win.dispatchEvent(new win.Event('ml:shipping-rules:ready'));
      } else if (doc && typeof doc.createEvent === 'function') {
        var evt = doc.createEvent('Event');
        evt.initEvent('ml:shipping-rules:ready', true, true);
        win.dispatchEvent(evt);
      }
    }
  } catch (e) {
    // Final fallback (older IE): avoid ReferenceError by using typeof checks
    try {
      if (doc && typeof doc.createEvent === 'function' && typeof win.dispatchEvent === 'function') {
        var evt2 = doc.createEvent('Event');
        evt2.initEvent('ml:shipping-rules:ready', true, true);
        win.dispatchEvent(evt2);
      }
    } catch (e2) {
      // No-op
    }
  }
})(typeof globalThis !== 'undefined' ? globalThis : (typeof window !== 'undefined' ? window : (typeof self !== 'undefined' ? self : {})));
